import React, { Fragment, useCallback, useEffect, useState } from "react";
import { head, map } from "ramda";
import Grid from "@material-ui/core/Grid";
import { Box, Card } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import { useDispatch } from "react-redux";
import { logOut } from "../auth/authActions";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { notBlank } from "../util/ext";
import DateRangeSelect, {
  calculateDate,
  dateRanges,
} from "../components/dashboard/DateRangeSelect";
import { useSearchQuery } from "../creator-application/common/use-searchQuery";
import { useSearch } from "../creator-application/searchServices";
import { normalizeFacetResults } from "../browse-media/redux/browseMediaHelpers";

const useStyles = makeStyles(theme => ({
  root: {
    width: 305,
    minHeight: 390,
    height: "100%",
    padding: theme.spacing(4),
    borderRadius: 20,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.primary.main,
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
  },

  h1: {
    fontSize: "3.6rem",
    lineHeight: "3.4rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },

  body1: {
    fontSize: "1.2rem",
    textTransform: "uppercase",
  },

  body2: {
    fontSize: "1.4rem",
    lineHeight: "3.4rem",
    color: theme.palette.secondary.contrastText,
  },

  rangeSelect: {
    minWidth: 95,
  },

  graph: {
    // width: "calc(100% + 75px)",
    width: "100%",
    height: "100%",
    marginTop: theme.spacing(1),
  },

  progressWrapper: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
  },
}));

const graphColors = {
  ALBUM: { color: "#03d3fc", label: "Albums" },
  ARTICLE: { color: "#00FFFF", label: "Articles" },
  AUDIO: { color: "#EFCBA1", label: "Audio" },
  AUDIOBOOK: { color: "#FF0000", label: "Audiobook" },
  BOOK: { color: "#D6EBD5", label: "Books" },
  CHAPTER: { color: "#FFFF00", label: "Chapters" },
  DISSERTATION: { color: "#00FF00", label: "Dissertations" },
  IMAGE: { color: "#C9AAD5", label: "Images" },
  OTHER_DOCUMENT: { color: "#0000FF", label: "Documents" },
  VIDEO: { color: "#AADAE0", label: "Videos" },
  NO_DATA: { color: "#CCCCCC" },
};

const initialDate = calculateDate(dateRanges.last_30);
const initialSearchState = {
  appliedQuery: [],
  appliedFacets: [
    {
      name: "type",
      queryParam: "_type",
      terms: [
        {
          code: "CONTENT",
        },
      ],
    },
  ],
  orderBy: "name",
  orderDirection: "asc",
  otherParams: [{ aa: `${initialDate}|` }],
  appliedPublicationYearGroups: [],
  page: 0,
  moduleOnly: false,
  titleOnly: false,
  limit: 0,
};

const RecentMediaReport = () => {
  const classes = useStyles();
  const reduxDispatch = useDispatch();
  const [totalCount, setTotalCount] = useState(0);
  const [graphData, setGraphData] = useState([]);

  //-- Handle Search state
  const { state, handleUpdateOtherParams } = useSearchQuery(initialSearchState);

  const searchResults = useSearch(state);

  useEffect(() => {
    if (searchResults.isError) {
      //-- Send user to login if the token has expired or not logged in
      if (searchResults.error.status === 401) {
        reduxDispatch(logOut());
      }
    }

    if (searchResults.isSuccess) {
      const facet = searchResults.data.facets.contentType;
      const contentTypesFacet = head(normalizeFacetResults([facet]));
      if (notBlank(contentTypesFacet)) {
        setGraphData(contentTypesFacet.terms);
        setTotalCount(searchResults.data.totalCount);
      } else {
        setGraphData([]);
        setTotalCount(0);
      }
    } else {
      setGraphData([]);
      setTotalCount(0);
    }
  }, [
    searchResults.isError,
    searchResults.isSuccess,
    searchResults.error,
    searchResults.data,
    reduxDispatch,
  ]);

  const handleDateRangeChange = useCallback(
    value => {
      console.log(`Search Date : ${value}`);
      handleUpdateOtherParams([{ aa: `${value}|` }]);
    },
    [handleUpdateOtherParams],
  );

  return (
    <Card classes={{ root: classes.root }}>
      <Grid container justify="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" component="span">
            Media Added
          </Typography>
        </Grid>

        <Grid item>
          <Grid container alignItems="center" spacing={5}>
            <Grid item>
              <DateRangeSelect
                initialRange={dateRanges.last_30}
                onDateRangeChange={handleDateRangeChange}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {!searchResults.isLoading ? (
        <Grid container spacing={5} justify="flex-start">
          <Grid item>
            <Box style={{ marginRight: 50 }}>
              <Typography
                variant="body1"
                component="div"
                color="primary"
                classes={{ body1: classes.body1 }}
              >
                Total
              </Typography>
              <Typography
                variant="h1"
                component="div"
                classes={{ h1: classes.h1 }}
              >
                {totalCount.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Typography
            variant="h1"
            component="span"
            classes={{ h1: classes.h1 }}
          >
            Loading...
          </Typography>
        </Box>
      )}

      {!searchResults.isLoading ? (
        <Fragment>
          <Box mt={1} mb={1} ml={"auto"} mr={"auto"} height={125} width={265}>
            <ResponsiveContainer height={"100%"} width={"100%"}>
              <PieChart>
                <Pie
                  blendStroke
                  data={notBlank(graphData) ? graphData : [{ count: 1 }]}
                  cx={"50%"}
                  cy={"50%"}
                  innerRadius={35}
                  outerRadius={60}
                  paddingAngle={0}
                  dataKey="count"
                >
                  {notBlank(graphData) ? (
                    graphData.map((entry, index) => {
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={graphColors[entry.code].color}
                        />
                      );
                    })
                  ) : (
                    <Cell key={`cell-0`} fill={graphColors["NO_DATA"].color} />
                  )}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Box>
            <Grid container spacing={2}>
              {map(
                type => (
                  <Grid key={type.code} item>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Box
                          bgcolor={graphColors[type.code].color}
                          height={15}
                          width={15}
                          borderRadius={5}
                        ></Box>
                      </Grid>
                      <Grid item>
                        <Typography
                          component="span"
                          variant="body2"
                          classes={{ body2: classes.body2 }}
                        >
                          {type.label}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ),
                graphData,
              )}
            </Grid>
          </Box>
        </Fragment>
      ) : (
        <Box className={classes.progressWrapper}>
          <PanelLoadingIndicator />
        </Box>
      )}
    </Card>
  );
};

export default RecentMediaReport;
